import { useQuery } from '@tanstack/react-query';
import { selectActiveContext, useAppSelector } from '@doccareplus/kernel/application';
import {
  schedulingApi,
  type AppointmentBracketLoadResponse,
  type AppointmentResponse,
  type GetAppointmentsParams,
  type PatientPickOption,
  type ProviderOption,
  type StaffBookingOptions,
  type QueueCopilotBoardResponse,
  type QueueVisitSnapshot,
  type IntakeFormResponse,
} from '../infrastructure/scheduling.api';

/**
 * Query keys for the scheduling feature. Commands invalidate against these so
 * the schedule and queue refetch after a write. Kept in one place so keys never drift.
 *
 * Read keys carry the active `clinicId` so one clinic's cached data can never be served to
 * another after a clinic switch. Invalidations omit `clinicId` and rely on React Query's prefix
 * matching (`['scheduling', 'queue']` matches `['scheduling', 'queue', clinicId]`).
 */
export const schedulingKeys = {
  all: ['scheduling'] as const,
  appointments: (params: GetAppointmentsParams, clinicId?: string) =>
    ['scheduling', 'appointments', ...(clinicId ? [clinicId] : []), params] as const,
  queue: (clinicId?: string) => ['scheduling', 'queue', ...(clinicId ? [clinicId] : [])] as const,
  requests: (clinicId?: string) => ['scheduling', 'requests', ...(clinicId ? [clinicId] : [])] as const,
  detail: (appointmentId: string) => ['scheduling', 'detail', appointmentId] as const,
  patientSearch: (term: string, clinicId?: string) =>
    ['scheduling', 'patient-search', ...(clinicId ? [clinicId] : []), term] as const,
  providers: (clinicId?: string) => ['scheduling', 'providers', ...(clinicId ? [clinicId] : [])] as const,
  visit: (visitId: string) => ['scheduling', 'visit', visitId] as const,
  intakeForm: (clinicId?: string) => ['scheduling', 'intake-form', ...(clinicId ? [clinicId] : [])] as const,
  bracketLoad: (providerId: string, startTime: string, clinicId?: string) =>
    ['scheduling', 'bracket-load', ...(clinicId ? [clinicId] : []), providerId, startTime] as const,
  bookingOptions: (date: string, providerId: string, durationMinutes: number, clinicId?: string) =>
    ['scheduling', 'booking-options', ...(clinicId ? [clinicId] : []), date, providerId, durationMinutes] as const,
  copilotBoard: (clinicId?: string) => ['scheduling', 'copilot-board', ...(clinicId ? [clinicId] : [])] as const,
  completedCount: (clinicId?: string) => ['scheduling', 'completed-count', ...(clinicId ? [clinicId] : [])] as const,
};

/** The active clinic id used to scope scheduling read caches. */
function useActiveClinicId(): string | undefined {
  return useAppSelector(selectActiveContext)?.clinicId;
}

/** Read — appointments in a UTC start-time window for the calendar/schedule views. */
export function useAppointmentsQuery(params: GetAppointmentsParams) {
  const clinicId = useActiveClinicId();
  return useQuery<AppointmentResponse[]>({
    queryKey: schedulingKeys.appointments(params, clinicId),
    queryFn: () => schedulingApi.list(params),
  });
}

/** Read — today's queue board (same-day appointment records, cancelled/rejected hidden). */
export function useQueueQuery(liveConnected = true) {
  const clinicId = useActiveClinicId();
  return useQuery<AppointmentResponse[]>({
    queryKey: schedulingKeys.queue(clinicId),
    queryFn: () => schedulingApi.getQueue(),
    refetchInterval: liveConnected ? false : 60_000,
  });
}

/**
 * Read — the true total of today's Completed queue rows. The board only carries the most recent page of
 * completed rows (so it scales past 1000/day); this cheap COUNT feeds the Completed lane's badge. Refetches on
 * the same cadence as the board.
 */
export function useQueueCompletedCountQuery(liveConnected = true) {
  const clinicId = useActiveClinicId();
  return useQuery<number>({
    queryKey: schedulingKeys.completedCount(clinicId),
    queryFn: async () => (await schedulingApi.getCompletedCount()).total,
    refetchInterval: liveConnected ? false : 60_000,
  });
}

/** Read — pending portal requests awaiting confirmation/rejection (Requested, any date). */
export function useAppointmentRequestsQuery() {
  const clinicId = useActiveClinicId();
  return useQuery<AppointmentResponse[]>({
    queryKey: schedulingKeys.requests(clinicId),
    queryFn: () => schedulingApi.getRequests(),
  });
}

/** Read one appointment by id, including records that are no longer on today's active queue. */
export function useAppointmentDetailQuery(appointmentId?: string) {
  return useQuery<AppointmentResponse>({
    queryKey: schedulingKeys.detail(appointmentId ?? ''),
    queryFn: () => schedulingApi.getById(appointmentId!),
    enabled: Boolean(appointmentId),
  });
}

/** Read — server-side patient search for the booking picker. Enabled once a term is entered. */
export function usePatientSearchQuery(term: string) {
  const clinicId = useActiveClinicId();
  const trimmed = term.trim();
  return useQuery<PatientPickOption[]>({
    queryKey: schedulingKeys.patientSearch(trimmed, clinicId),
    queryFn: () => schedulingApi.searchPatients(trimmed),
    enabled: trimmed.length >= 2,
  });
}

/** Read — clinic staff as bookable providers for the provider picker. */
export function useProvidersQuery() {
  const clinicId = useActiveClinicId();
  return useQuery<ProviderOption[]>({
    queryKey: schedulingKeys.providers(clinicId),
    queryFn: () => schedulingApi.listProviders(),
  });
}

/**
 * Read — the Smart Queue copilot board: queue rows with wait estimates plus the active recommendations and
 * assistance status flags. Clinic-scoped so one clinic's recommendations are never served to another.
 */
export function useQueueCopilotBoardQuery(liveConnected = true) {
  const clinicId = useActiveClinicId();
  return useQuery<QueueCopilotBoardResponse>({
    queryKey: schedulingKeys.copilotBoard(clinicId),
    queryFn: () => schedulingApi.getCopilotBoard(),
    refetchInterval: liveConnected ? false : 60_000,
  });
}

/**
 * Read — advisory appointment-overload check for a candidate (provider, start time). Only meaningful for
 * a concrete provider + scheduled start time (see .ai/BusinessDomain/scheduling-queue.md, "Appointment
 * Overload" — "any doctor" and walk-ins are never checked in v1); pass `enabled: false` for those cases.
 */
export function useBracketLoadQuery(providerId: string, startTime: string, enabled: boolean) {
  const clinicId = useActiveClinicId();
  return useQuery<AppointmentBracketLoadResponse>({
    queryKey: schedulingKeys.bracketLoad(providerId, startTime, clinicId),
    queryFn: () => schedulingApi.getBracketLoad(providerId, startTime),
    enabled: enabled && Boolean(providerId) && Boolean(startTime),
  });
}

export function useQueueVisitQuery(visitId?: string) {
  return useQuery<QueueVisitSnapshot>({
    queryKey: schedulingKeys.visit(visitId ?? ''),
    queryFn: () => schedulingApi.getVisit(visitId!),
    enabled: Boolean(visitId),
  });
}

export function useStaffBookingOptionsQuery(
  date: string,
  providerId: string,
  durationMinutes: number,
  enabled: boolean,
) {
  const clinicId = useActiveClinicId();
  return useQuery<StaffBookingOptions>({
    queryKey: schedulingKeys.bookingOptions(date, providerId, durationMinutes, clinicId),
    // A five-minute bootstrap read exposes the clinic policy before staff choose a duration. The
    // picker hides those provisional slots; selecting a duration changes the key and recalculates them.
    queryFn: () => schedulingApi.getBookingOptions(date, providerId, durationMinutes || 5),
    enabled: enabled && Boolean(date),
  });
}

export function useIntakeFormQuery() {
  const clinicId = useActiveClinicId();
  return useQuery<IntakeFormResponse>({
    queryKey: schedulingKeys.intakeForm(clinicId),
    queryFn: () => schedulingApi.getIntakeForm(),
    staleTime: 5 * 60_000,
  });
}

export function useQuickIntakeQuery(visitId: string) {
  const clinicId = useActiveClinicId();
  return useQuery<{ visit: QueueVisitSnapshot; form: IntakeFormResponse }>({
    queryKey: ['scheduling', 'quick-intake', clinicId, visitId],
    queryFn: async () => {
      const [visit, form] = await Promise.all([
        schedulingApi.getVisit(visitId),
        schedulingApi.getIntakeForm(),
      ]);
      return { visit, form };
    },
    enabled: Boolean(visitId),
  });
}
